import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "./Modal";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useModalNavigation } from "@/hooks/useModalNavigation";
import { useUpsertUser } from "@/hooks/queryHooks";
import { z } from "zod/v4";
import { zodResolver } from "@hookform/resolvers/zod";

const formSchema = z.object({
  username: z.string(),
  first_name: z.optional(z.string()),
  last_name: z.optional(z.string()),
  profile_image_url: z.optional(z.url()),
  email: z.email(),
});

export const NewUserModal = () => {
  const { closeModal, getModalState } = useModalNavigation();
  const modalState = getModalState();
  const accountId = modalState?.accountId;
  const createUserMutation = useUpsertUser();
  const { error: mutationError, isPending } = createUserMutation;

  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const handleCancel = () => {
    closeModal();
    form.reset();
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await createUserMutation.mutateAsync({
        ...values,
        account_id: accountId || "",
      });
      closeModal();
      form.reset();
    } catch (err) {
      console.error("Failed to create user:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  };

  useEffect(() => {
    if (mutationError) {
      setError(mutationError.message);
    }
  }, [mutationError]);

  return (
    <Modal
      className="overflow-y-scroll max-h-full"
      title="Create New User"
      description="Fill out the information below to create a new user in the system."
      onClose={handleCancel}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
          {error}
        </div>
      )}
      <Form {...form}>
        <form
          className="space-y-4 md:grid md:grid-cols-2 md:gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem aria-required={true}>
                <FormLabel>Username *</FormLabel>
                <FormControl>
                  <Input required {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem aria-required={true}>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input required type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="profile_image_url"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Profile Image URL</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end space-x-2 pt-4 md:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              className="bg-lime text-foreground cursor-pointer hover:text-background"
              type="submit"
              disabled={isPending}
            >
              {isPending ? "Creating..." : "Create User"}
            </Button>
          </div>
        </form>
      </Form>
    </Modal>
  );
};
